import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Minus, Plus } from 'lucide-react';
import { PageHeader, Stat, Stats, DataTable, FilterBar } from '../../components/office';
import { stockTakesApi, stockApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Segmented } from '@verlo/shared/components/ui/segmented';
import { SearchField } from '@verlo/shared/components/ui/search-field';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@verlo/shared/components/ui/dialog';
import { useAuthoringSite } from '../../contexts/SiteContext';
import { qty } from '../../lib/units';

// Stock takes (Back office sweep, Oct 2026). Count what's actually there and settle the
// difference: each take is a row, and opens to show what was out. Counting is one table,
// every item starting at what the system expects; change the ones that differ, and say why.

const money = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);
const signed = (v) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${money(Math.abs(v))}`;
const when = (iso) => new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const round3 = (n) => Math.round(n * 1000) / 1000;
// Older takes kept the miscount unsigned; the sign is the variance's.
const lineValue = (l) => (l.variance_value ?? Math.sign(l.variance || 0) * (l.variance_cost || 0));
// Summed from the lines: the API gives older takes a total of 0.
const netOf = (t) => Math.round((t.items || []).reduce((s, l) => s + lineValue(l), 0) * 100) / 100;
const tone = (v) => (v < 0 ? 'text-signal-stop' : v > 0 ? 'text-signal-ok' : 'text-ink-soft');

const StockTakesPage = () => {
  const authoring = useAuthoringSite();
  const [takes, setTakes] = useState(null);
  const [stock, setStock] = useState([]);
  const [counting, setCounting] = useState(null);   // { counts: {id: value}, notes: {id: note}, note }
  const [viewing, setViewing] = useState(null);
  const [search, setSearch] = useState('');
  const [show, setShow] = useState('all');
  const [saving, setSaving] = useState(false);

  const load = () => Promise.all([stockTakesApi.getAll(20), stockApi.getAll()])
    .then(([t, s]) => { setTakes(t.data || []); setStock(s.data || []); })
    .catch(() => { setTakes([]); toast.error("Couldn't load stock takes"); });
  useEffect(() => { load(); }, []);

  const byId = useMemo(() => Object.fromEntries(stock.map((s) => [s.id, s])), [stock]);
  const all = takes || [];
  const last = all[0];

  const start = () => { setSearch(''); setShow('all'); setCounting({ counts: Object.fromEntries(stock.map((s) => [s.id, String(s.quantity)])), notes: {}, note: '' }); };
  const diffOf = (s) => round3((Number(counting.counts[s.id]) || 0) - Number(s.quantity || 0));
  const setCount = (id, v) => setCounting((c) => ({ ...c, counts: { ...c.counts, [id]: v } }));
  const differing = counting ? stock.filter((s) => diffOf(s) !== 0) : [];
  const countValue = differing.reduce((t, s) => t + diffOf(s) * (Number(s.cost_per_unit) || 0), 0);
  const rows = counting ? stock.filter((s) => (show === 'all' || diffOf(s) !== 0) && (!search.trim() || s.name.toLowerCase().includes(search.trim().toLowerCase()))) : [];

  const submit = async () => {
    if (!differing.length && !counting.note.trim()) { toast.error('Nothing differs. Change a count, or add a note to record a clean take'); return; }
    setSaving(true);
    try {
      await stockTakesApi.create({
        items: stock.map((s) => ({
          stock_item_id: s.id, counted_quantity: Number(counting.counts[s.id]) || 0, expected_quantity: Number(s.quantity) || 0,
          variance: diffOf(s), notes: counting.notes[s.id] || '',
        })),
        notes: counting.note.trim(),
      }, authoring.site);
      toast.success(differing.length ? `Stock take done: ${differing.length} item${differing.length === 1 ? '' : 's'} corrected` : 'Stock take done: everything matched');
      setCounting(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the stock take"); } finally { setSaving(false); }
  };

  const columns = [
    { key: 'created_at', label: 'Counted', sortable: true, render: (t) => (
      <div><div className="font-medium text-ink">{when(t.created_at)}</div>{t.notes && <div className="max-w-md truncate text-[12.5px] text-ink-soft">{t.notes}</div>}</div>
    ) },
    { key: 'items', label: 'Items', align: 'right', mono: true, sortValue: (t) => t.items?.length || 0, render: (t) => t.items?.length || 0 },
    { key: 'out', label: 'Out', align: 'right', mono: true, sortValue: (t) => (t.items || []).filter((l) => l.variance).length, render: (t) => {
      const n = (t.items || []).filter((l) => l.variance).length;
      return n ? n : <span className="text-ink-faint">none</span>;
    } },
    { key: 'created_by', label: 'By', muted: true },
    { key: 'net', label: 'Difference', align: 'right', mono: true, sortable: true, sortValue: netOf, render: (t) => <span className={tone(netOf(t))}>{signed(netOf(t))}</span> },
  ];

  return (
    <div className="space-y-5" data-testid="stock-takes-page">
      <PageHeader
        title="Stock takes"
        purpose="Count what is actually there, and settle the difference."
        actions={<Button className="bg-verlo hover:bg-verlo-hover" onClick={start} disabled={!stock.length || !authoring.ready} title={authoring.reason || undefined} data-testid="new-stock-take-btn"><Plus size={16} className="mr-2" />Start a count</Button>}
      />

      <Stats>
        <Stat label="Last count" value={takes ? (last ? new Date(last.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'Never') : null} hint={last?.created_by} />
        <Stat label="Last difference" value={takes ? (last ? signed(netOf(last)) : '—') : null} />
        <Stat label="Counts" value={takes ? all.length : null} hint="The last 20" />
        <Stat label="Net, all of them" value={takes ? signed(all.reduce((s, t) => s + netOf(t), 0)) : null} />
      </Stats>

      <DataTable
        columns={columns} rows={all} rowKey="id" loading={takes === null}
        defaultSort={{ key: 'created_at', dir: 'desc' }}
        onRowClick={setViewing}
        rowTestId={(t) => `stock-take-${t.id}`}
        empty={{ word: 'Stock takes', title: 'Nothing counted yet', children: 'Start a count: every item starts at what Verlo expects, and you change the ones that differ.' }}
        testId="stock-takes-table"
      />

      {/* Counting */}
      <Dialog open={counting !== null} onOpenChange={(o) => !o && setCounting(null)}>
        <DialogContent size="sheet">
          <DialogHeader>
            <DialogTitle>Count the stock</DialogTitle>
            <DialogDescription>Each item starts at what Verlo expects. Change the ones that differ; saving sets stock to what you counted.</DialogDescription>
          </DialogHeader>
          {counting && (
            <>
              <FilterBar right={<span>{differing.length} out · <span className={tone(countValue)}>{signed(countValue)}</span></span>}>
                <SearchField className="w-56" placeholder="Find an item…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="stock-take-search" />
                <Segmented value={show} onChange={setShow} options={[{ value: 'all', label: 'All' }, { value: 'out', label: 'Only what’s out' }]} />
              </FilterBar>
              <DialogBody className="p-0">
                <table className="w-full text-[13.5px]">
                  <thead className="sticky top-0 z-10 bg-surface-2"><tr className="text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                    <th className="px-4 py-2">Item</th><th className="px-3 py-2">Expected</th><th className="px-3 py-2">Counted</th><th className="px-4 py-2 text-right">Difference</th></tr></thead>
                  <tbody>
                    {rows.map((s) => {
                      const d = diffOf(s);
                      const step = (dir) => setCount(s.id, String(Math.max(0, round3((Number(counting.counts[s.id]) || 0) + dir))));
                      return (
                        <tr key={s.id} className={`border-t border-hairline align-top ${d ? 'bg-signal-warn-tint/40' : ''}`} data-testid={`stock-item-${s.id}`}>
                          <td className="px-4 py-2">
                            <div className="font-medium text-ink">{s.name}</div>
                            {d !== 0 && (
                              <Input value={counting.notes[s.id] || ''} onChange={(e) => setCounting((c) => ({ ...c, notes: { ...c.notes, [s.id]: e.target.value } }))}
                                placeholder="Why? e.g. two went to staff food" maxLength={200} className="mt-1.5 h-8 text-[13px]" data-testid={`note-input-${s.id}`} />
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono tabular-nums text-ink-soft">{qty(s.quantity, s.unit)}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => step(-1)} aria-label={`One less ${s.name}`}><Minus size={14} /></Button>
                              <Input type="number" min="0" step="any" value={counting.counts[s.id] ?? ''} onChange={(e) => setCount(s.id, e.target.value)}
                                className="h-8 w-20 text-center font-mono tabular-nums" aria-label={`${s.name} counted`} data-testid={`count-input-${s.id}`} />
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => step(1)} aria-label={`One more ${s.name}`}><Plus size={14} /></Button>
                              <span className="text-[12.5px] text-ink-soft">{s.unit === 'each' ? '' : s.unit}</span>
                            </div>
                          </td>
                          <td className={`px-4 py-2 text-right font-mono tabular-nums ${tone(d)}`}>
                            {d ? <>{d > 0 ? '+' : '−'}{qty(Math.abs(d), s.unit)}<div className="text-[12px]">{signed(d * (Number(s.cost_per_unit) || 0))}</div></> : <span className="text-ink-faint">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {!rows.length && <tr><td colSpan={4} className="px-4 py-6 text-center text-[13px] text-ink-soft">{show === 'out' ? 'Nothing is out yet.' : 'Nothing matches.'}</td></tr>}
                  </tbody>
                </table>
              </DialogBody>
              <div className="space-y-1.5 border-t border-hairline px-6 pt-3">
                <Label className="text-[12.5px] text-ink-soft">A note on this count <span className="text-ink-faint">(optional)</span></Label>
                <Textarea rows={2} value={counting.note} onChange={(e) => setCounting({ ...counting, note: e.target.value })} maxLength={500} placeholder="e.g. Monday close, walk-in and dry store" data-testid="stock-take-notes" />
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCounting(null)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={submit} disabled={saving} data-testid="submit-stock-take-btn">{saving ? 'Saving…' : 'Save the count'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One take */}
      <Dialog open={viewing !== null} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent size="sheet">
          <DialogHeader>
            <DialogTitle>Count of {viewing && when(viewing.created_at)}</DialogTitle>
            <DialogDescription>
              {viewing && `${viewing.items?.length || 0} items counted by ${viewing.created_by || 'someone'}; ${(viewing.items || []).filter((l) => l.variance).length} out, ${signed(netOf(viewing))} in all.`}
            </DialogDescription>
          </DialogHeader>
          {viewing && (
            <DialogBody className="p-0">
              {viewing.notes && <p className="border-b border-hairline px-6 py-3 text-[13.5px] text-ink-soft">{viewing.notes}</p>}
              <table className="w-full text-[13.5px]">
                <thead className="sticky top-0 bg-surface-2"><tr className="text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                  <th className="px-6 py-2">Item</th><th className="px-3 py-2">Expected</th><th className="px-3 py-2">Counted</th><th className="px-6 py-2 text-right">Difference</th></tr></thead>
                <tbody>
                  {[...(viewing.items || [])].sort((a, b) => Math.abs(lineValue(b)) - Math.abs(lineValue(a))).map((l) => {
                    const unit = byId[l.stock_item_id]?.unit;
                    const v = lineValue(l);
                    return (
                      <tr key={l.stock_item_id} className="border-t border-hairline align-top">
                        <td className="px-6 py-2"><div className="text-ink">{l.stock_item_name || l.name}</div>{l.notes && <div className="text-[12.5px] text-ink-soft">{l.notes}</div>}</td>
                        <td className="px-3 py-2 font-mono tabular-nums text-ink-soft">{qty(l.expected_quantity, unit)}</td>
                        <td className="px-3 py-2 font-mono tabular-nums text-ink">{qty(l.counted_quantity ?? l.actual_quantity, unit)}</td>
                        <td className={`px-6 py-2 text-right font-mono tabular-nums ${tone(v)}`}>{l.variance ? signed(v) : <span className="text-ink-faint">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </DialogBody>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setViewing(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockTakesPage;
